import { Clock, CheckCircle2, XCircle, AlertCircle, RefreshCw, Phone, Mail, LogOut, ArrowRight } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { auth } from '../../config/firebase.config';
import { signOut } from 'firebase/auth';
import { dbService } from '../../services/firebase-db.service';

interface ApplicationReviewScreenProps {
  onComplete: () => void;
}

type DocumentStatus = 'pending' | 'approved' | 'rejected';

interface DocumentVerification {
  name: string;
  status: DocumentStatus;
  reason?: string;
}

export function ApplicationReviewScreen({ onComplete }: ApplicationReviewScreenProps) {
  const [applicationStatus, setApplicationStatus] = useState<'pending' | 'approved' | 'rejected' | 'loading'>('loading');
  const [riderName, setRiderName] = useState('Rider');
  const [submittedAt, setSubmittedAt] = useState('');
  const advancedRef = useRef(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = dbService.subscribeToRiderProfile(user.uid, (profile) => {
      if (!profile) {
        console.error('⚠️ Profile missing in Application Review screen');
        signOut(auth).then(() => window.location.reload());
        return;
      }

      const status = profile.verificationStatus || 'pending';
      setApplicationStatus(status);
      setRiderName(profile.name);

      if (profile.createdAt) {
        setSubmittedAt(new Date(profile.createdAt).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        }));
      }

      // As soon as admin approves, move rider forward — no waiting or manual refresh
      if (status === 'approved' && !advancedRef.current) {
        advancedRef.current = true;
        onComplete();
      }
    });

    return () => unsubscribe();
  }, [onComplete]);

  const handleLogout = async () => {
    if (confirm('Are you sure you want to logout?')) {
      try {
        await signOut(auth);
        window.location.reload();
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }
  };

  const documents: DocumentVerification[] = [
    { name: 'Profile Photo', status: 'pending' },
    { name: 'Government ID', status: 'pending' },
    { name: 'Selfie with ID', status: 'pending' },
    { name: 'Vehicle Documents', status: 'pending' },
  ];

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="w-5 h-5" />;
      case 'rejected': return <XCircle className="w-5 h-5" />;
      case 'pending': return <Clock className="w-5 h-5" />;
    }
  };

  return (
    <div className="absolute inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-b from-[#E0001B]/5 to-transparent">
        <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mb-6 mx-auto">
          <Clock className="w-10 h-10 text-yellow-600" />
        </div>
        
        <h1 className="text-[28px] text-[#111111] mb-2 text-center">
          {applicationStatus === 'approved' ? 'Application Approved!' : 'Application Under Review'}
        </h1>
        
        <p className="text-[#6B6B6B] text-center max-w-md mx-auto">
          {applicationStatus === 'approved'
            ? 'You are approved! Taking you to training now...'
            : 'Thank you for applying! You will be activated instantly when an admin approves your account.'}
        </p>
        
        {applicationStatus === 'pending' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-[12px] max-w-md mx-auto">
            <p className="text-[12px] text-blue-800 text-center">
              This page updates automatically — no need to refresh.
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 px-8 py-6 overflow-auto">

        {/* Approved Banner */}
        {applicationStatus === 'approved' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-[12px]">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
              <p className="text-[16px] font-medium text-green-800">Application Approved!</p>
            </div>
            <p className="text-[14px] text-green-700 mb-4">
              Congratulations {riderName}! Opening training now...
            </p>
          </div>
        )}

        {/* Rejected Banner */}
        {applicationStatus === 'rejected' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-[12px]">
            <div className="flex items-center gap-3 mb-2">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <p className="text-[16px] font-medium text-red-800">Application Not Approved</p>
            </div>
            <p className="text-[14px] text-red-700">
              Unfortunately your application was not approved at this time. Please contact support for more information.
            </p>
          </div>
        )}

        {/* Application Reference */}
        <div className="p-4 bg-gray-50 rounded-[12px] mb-6">
          <p className="text-[12px] text-[#6B6B6B] mb-1">Applicant</p>
          <p className="text-[18px] font-medium text-[#111111]">{riderName}</p>
          {submittedAt && (
            <p className="text-[12px] text-[#6B6B6B] mt-2">Submitted on {submittedAt}</p>
          )}
          <p className="text-[12px] text-yellow-600 font-medium mt-2">
            Status: {applicationStatus === 'loading' ? 'Checking...' : (applicationStatus || 'pending').toUpperCase()}
          </p>
        </div>

        {/* Document Verification Status */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">Document Verification</h2>
          <div className="space-y-3">
            {documents.map((doc, index) => (
              <div
                key={index}
                className={`p-4 rounded-[12px] border ${getStatusColor(doc.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(doc.status)}
                    <div>
                      <p className="text-[14px] font-medium">{doc.name}</p>
                      {doc.reason && (
                        <p className="text-[12px] mt-0.5">{doc.reason}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-[12px] font-medium capitalize">{doc.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What's Next */}
        <div className="p-6 bg-blue-50 rounded-[12px] border border-blue-100 mb-6">
          <h3 className="text-[16px] font-medium text-blue-900 mb-3">What happens next?</h3>
          <ul className="space-y-2 text-[14px] text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">1.</span>
              <span>Our team reviews your documents (usually within 24 hours)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">2.</span>
              <span>When approved, this screen updates instantly and training opens automatically</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">3.</span>
              <span>Complete training and start accepting delivery jobs</span>
            </li>
          </ul>
        </div>

        {/* Support */}
        <div className="p-4 bg-gray-50 rounded-[12px] mb-6">
          <h3 className="text-[14px] font-medium mb-3">Need help?</h3>
          <div className="space-y-2">
            <a href="tel:+2348000000000" className="flex items-center gap-2 text-[14px] text-[#E0001B]">
              <Phone className="w-4 h-4" />
              Call Support
            </a>
            <a href="mailto:support@go7.ng" className="flex items-center gap-2 text-[14px] text-[#E0001B]">
              <Mail className="w-4 h-4" />
              support@go7.ng
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-200 space-y-3">
        <button
          onClick={handleLogout}
          className="w-full h-12 rounded-[12px] border border-gray-200 text-[#6B6B6B] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}
